import React, { useEffect, useRef, useState } from "react";
import { useTopTokensByHolders } from "../../../hooks"; // Removed unused useLargeSellTrades, useTopTraders. Adjust the import path if needed.
import {
  Avatar,
  Skeleton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  Button,
  Divider,
  Stack,
  // Removed unused Pagination import unless uncommenting pagination controls
} from "@mui/material";
import moment from "moment";
import { useTranslation } from 'react-i18next';
// Removed unused uuidv4 import from "@walletconnect/utils"
import QuickBuy from "../../Common/QuickBuy.component"; // Adjust the import path if needed
import { useNavigate } from "react-router-dom";
import { useBitcoinPrice } from '../../../state/context/bitcoin.context'; // Adjust path if needed
import { ScoreCell } from "../../ActivitiesFeed/EventStream.component";
import { formatTokenAmount } from "../../../utils/helpers";

// Import moment locales
import 'moment/locale/zh-cn';

// Removed unused transformToBTC function

const CollapsibleTableRow = ({ token }) => {
  const { i18n } = useTranslation();
  const { price: btcPrice } = useBitcoinPrice(); // Renamed for clarity to avoid conflict if needed elsewhere

  // Set moment locale based on current language
  useEffect(() => {
    const locale = i18n.language === 'zh' ? 'zh-cn' : 'en';
    moment.locale(locale);
  }, [i18n.language]);

  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  const handleMouseEnter = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setOpen(true);
  };

  const handleMouseLeave = () => {
    timerRef.current = setTimeout(() => {
      setOpen(false);
    }, 100); // Adjust delay as needed
  };

  useEffect(() => {
    // Cleanup timer on unmount
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Pre-calculate numeric values to avoid string multiplication issues
  const marketCapBTC = (token.price * 21000000) / 1e11;
  const volumeBTC = token.volume / 1e11;
  const marketCapUSD = btcPrice ? marketCapBTC * btcPrice : null;
  const volumeUSD = btcPrice ? volumeBTC * btcPrice : null;

  return (
    <TableRow
      // Key prop is applied in the parent component's map function
      onClick={() => window.open(`/tokens/${ token.token }`)} // Navigate on row click
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        cursor: "pointer",
        position: 'relative', // Add relative positioning to the row
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.1) !important",
        },
        backgroundColor: open ? "rgba(255, 255, 255, 0.05)" : "inherit",
        transition: "background-color 0.2s ease-in-out",
      }}
    >
      {/* Token Cell */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            alt={token.name}
            src={`https://images.odin.fun/token/${ token.token }`}
            sx={{ width: 24, height: 24 }}
          />
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
            {token.ticker}
          </Typography>
        </Box>
      </TableCell>

      <TableCell align="center">
        <ScoreCell userScore={token.userScore.creatorScore} />
      </TableCell>

      <TableCell align="right">{token.holderCount}</TableCell>

      <TableCell align="right">
        {token.creatorBalance && token.creatorBalance.airdroppedSent ? formatTokenAmount(token.creatorBalance.airdroppedSent) : 0}
      </TableCell>

      {/* Volume Cell */}
      <TableCell align="right">
        <Stack direction="column" spacing={0.5} alignItems="flex-end">
          <Typography variant="body2">{volumeBTC.toFixed(4)} BTC</Typography>
          {volumeUSD !== null && (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
              {volumeUSD.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </Typography>
          )}
        </Stack>
      </TableCell>

      {/* Market Cap Cell */}
      <TableCell align="right">
        <Stack direction="column" spacing={0.5} alignItems="flex-end">
          <Typography variant="body2">{marketCapBTC.toFixed(4)} BTC</Typography>
          {marketCapUSD !== null && (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
              {marketCapUSD.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </Typography>
          )}
        </Stack>
      </TableCell>

      {/* Time Created Cell */}
      <TableCell align="right">{moment(token.time).fromNow()}</TableCell>

      {/* Collapsible Content - Positioned absolutely to span full row width */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 10,
            // backgroundColor: 'rgba(0, 0, 0, 0.9)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 1,
            p: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60px',
            //add blur effect
            backdropFilter: 'blur(10px)',
            // backgroundColor: 'rgba(0, 0, 0, 0.8)',

          }}
        >
          <QuickBuy tokenId={token.token} />
        </Box>
      </Collapse>
    </TableRow>
  );
};


const TopTokens = ({ selectedTimeOffset }) => {
  const { t } = useTranslation();
  // NOTE: Pagination state (page, setPage, hasNextPage) is fetched but controls are commented out below.
  const { data, loading, error, page, setPage, hasNextPage } =
    useTopTokensByHolders(selectedTimeOffset, 10); // Assuming page size is 10

  if (loading)
    return (
      <Skeleton
        variant="rectangular"
        width={"100%"}
        height={300}
        sx={{ flexGrow: 1, borderRadius: 1 }} // Added slight rounding
      />
    );
  if (error)
    return (
      <Typography color="error" align="center" sx={{ p: 2 }}>
        {t('common.error')}: {typeof error === 'string' ? error : error?.message || t('messages.failedToFetchToken')}
      </Typography>
    );
  return (
    <Box sx={{ width: "100%", mx: "auto", overflow: "hidden", pb: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
      {data.length === 0 ? (
        <Typography align="center">{t('common.noDataAvailable')}</Typography>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            // "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)": {
            //   backgroundColor: "rgba(255, 255, 255, 0.02)",
            // },
            // "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
            //   backgroundColor: "rgba(255, 255, 255, 0.05)",
            // },
            // "& .MuiTableBody-root .MuiTableRow-root:hover": {
            //   backgroundColor: "rgba(255, 255, 255, 0.1) !important",
            // },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {/* Use sx prop for styling consistency */}
                <TableCell sx={{ backgroundColor: "black", fontWeight: "bold" }}>
                  {t('token.name')}
                </TableCell>
                {/* <TableCell sx={{ backgroundColor: "black", fontWeight: "bold" }} align="right">
                  Creator Score
                </TableCell> */}
                <TableCell sx={{ backgroundColor: "black", fontWeight: "bold" }} align="center">
                  {t('user.scores')}
                </TableCell>
                <TableCell sx={{ backgroundColor: "black", fontWeight: "bold" }} align="right">
                  {t('token.holders')}
                </TableCell>
                <TableCell sx={{ backgroundColor: "black", fontWeight: "bold" }} align="right">
                  {t('table.airdropSent')}
                </TableCell>
                <TableCell sx={{ backgroundColor: "black", fontWeight: "bold" }} align="right">
                  {t('table.totalVolume')} (24h) {/* Clarify volume period if known */}
                </TableCell>
                <TableCell sx={{ backgroundColor: "black", fontWeight: "bold" }} align="right">
                  {t('token.marketCap')}
                </TableCell>
                <TableCell sx={{ backgroundColor: "black", fontWeight: "bold" }} align="right">
                  {t('token.createdAt')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((token) => (
                // FIX: Use a stable, unique key from the data (e.g., token.token or token._id)
                // Using uuidv4() or index is an anti-pattern in React.
                <CollapsibleTableRow token={token} key={token.token} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination Controls (Currently Commented Out) */}
      {/*
      {data.length > 0 && ( // Only show pagination if there's data
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
             // Note: The 'count' prop ideally needs total pages.
             // This logic just ensures the "next" button works with hasNextPage.
             // You might need totalItems / itemsPerPage from your hook for accurate page count display.
            count={hasNextPage ? page + 1 : page}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary" // Consider theming 'primary' or using 'standard'
            size="small"
            disabled={loading} // Disable pagination while loading new data
            showFirstButton={page > 1} // Optionally show first/last buttons
            showLastButton={hasNextPage} // Adjust based on total pages if available
          />
        </Box>
      )}
      */}
    </Box>
  );
};

export default TopTokens;